/* one delegated controller for every flower card on the page.
   The flower-only asset is loaded once by snippets/tk-card-assets.liquid.

   CHOOSE-FLOW (2026-07-16): flower cards now follow Horizon's own quick-add
   contract — `data-quick-add-button` is "choose" until a colour is committed
   (server renders it that way on collection pages), and any commitment (tile
   dot, colour-rail circle via tk-color-rail, or a pick inside the tk sheet)
   flips it to "add" + rewrites the native Add label to "Add · <Colour>".
   The native Choose button is redirected to the tk colour sheet by
   OVERRIDING each flower quick-add-component's `handleClick` INSTANCE
   property (never calling the original — it fetches the PDP and ALWAYS
   opens the stock dialog, empty for flowers: template_suffix 'flower'
   pages carry no [data-product-grid-content] for it to morph). A capture
   listener cannot do this job: Horizon routes every on:click through ONE
   document-level CAPTURE listener registered when component.js loads —
   before this deferred asset — and same-node capture listeners fire in
   registration order, so Horizon's dispatcher always runs first
   (2026-07-16 empty-modal-behind-the-sheet bug). The dispatcher resolves
   `instance[method]` at click time (component.js ~:307), so the instance
   override wins regardless of listener order. tk-color-rail calls
   window.tkFlcUnselect(root) to return cards to the unresolved state when
   the shopper goes back to "All". */
if (!window.__tkFlc) {
  window.__tkFlc = 1;

  var modal = null;
  var modalGrid = null;
  var modalPreview = null;
  var modalKicker = null;
  var modalTitle = null;
  var modalPrice = null;
  var modalOption = null;
  var modalChosen = null;
  var modalAdd = null;
  var modalLink = null;
  var modalStatus = null;
  var modalClose = null;
  var activeRoot = null;
  var activeCard = null;
  var activeOpener = null;
  var activeSource = null;
  var activeLabels = null;
  var lockedScroller = null;
  var lockedOverflow = '';
  var cartListener = null;
  var cartTimeout = null;
  var feedbackTimeout = null;
  var addToken = 0;

  customElements.whenDefined('product-card').then(function () {
    var PC = customElements.get('product-card');
    if (!PC || PC.__tkFlowerHoverPatch) return;
    PC.__tkFlowerHoverPatch = 1;
    var origPreview = PC.prototype.previewImage;
    var origReset = PC.prototype.resetImage;
    if (!origPreview || !origReset) {
      console.warn('[tk-flcard] Horizon previewImage/resetImage missing — re-check the flower hover guard.');
    }
    if (origPreview) PC.prototype.previewImage = function (event) {
      if (this.querySelector('[data-tk-flc]')) return;
      return origPreview.call(this, event);
    };
    if (origReset) PC.prototype.resetImage = function (event) {
      if (this.querySelector('[data-tk-flc]')) return;
      return origReset.call(this, event);
    };
  });

  function urlPath(href) {
    try { return new URL(href, window.location.href).pathname; } catch (error) { return ''; }
  }

  /* Repoint every link inside `card` that points at this product at `href`,
     remembering the original so unselectCard can put it back. The swatches —
     which each carry their own variant — are skipped. */
  function retargetCardLinks(card, href) {
    var path = urlPath(href);
    if (!path) return;
    card.querySelectorAll('a[href]').forEach(function (a) {
      if (a.hasAttribute('data-tk-flc-sw')) return;
      if (urlPath(a.getAttribute('href')) !== path) return;
      if (a.dataset.tkOrigHref === undefined) a.dataset.tkOrigHref = a.getAttribute('href') || '';
      a.href = href;
    });
  }

  function restoreCardLinks(card) {
    card.querySelectorAll('a[data-tk-orig-href]').forEach(function (a) {
      a.href = a.dataset.tkOrigHref;
    });
  }

  function selectSwatch(sw) {
    if (!sw || sw.dataset.available === 'false') return false;
    var root = sw.closest('[data-tk-flc]');
    if (!root) return false;
    root.querySelectorAll('[data-tk-flc-sw]').forEach(function (item) {
      var selected = item === sw;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    var price = root.querySelector('[data-tk-flc-price]');
    if (price && sw.dataset.price) {
      if (price.dataset.tkOrigPrice === undefined) price.dataset.tkOrigPrice = price.textContent;
      price.textContent = sw.dataset.price;
    }
    var name = root.querySelector('[data-tk-flc-selname]');
    if (name && sw.dataset.name) {
      name.textContent = sw.dataset.name;
      name.classList.remove('is-unset');
    }
    var link = root.querySelector('[data-tk-flc-name]');
    if (link) {
      if (link.dataset.tkOrigHref === undefined) link.dataset.tkOrigHref = link.getAttribute('href') || '';
      link.href = sw.getAttribute('href');
    }
    var card = root.closest('product-card');
    // Horizon renders more than one link per card: the invisible full-surface
    // overlay (.product-card__link) AND a second anchor wrapping the gallery
    // photo (ref="cardGalleryLink") which sits ON TOP of it. Retargeting only
    // the overlay left a click on the PHOTO — the biggest target on the card —
    // opening the product's default colour and throwing away the pick (found
    // alongside the bouquet-card fix, 2026-07-28).
    if (card) retargetCardLinks(card, sw.getAttribute('href'));
    var idInput = card && card.querySelector('quick-add-component form input[name="id"]');
    if (idInput && sw.dataset.vid) {
      if (idInput.dataset.tkOrigVid === undefined) idInput.dataset.tkOrigVid = idInput.value;
      idInput.value = sw.dataset.vid;
      idInput.disabled = false;
    }
    // a committed colour arms Horizon's native one-tap Add (its own contract:
    // base.css shows exactly one of the two stock buttons off this attribute)
    var qa = card && card.querySelector('quick-add-component');
    if (qa) qa.setAttribute('data-quick-add-button', 'add');
    // label polish: "Add · Blue". Horizon never rewrites this span (errors and
    // the Added-flash use separate elements) — and if a future update
    // restructures the button, we simply skip and it keeps saying "Add".
    var addLabel = card && card.querySelector('add-to-cart-component .add-to-cart-text__content span span');
    if (addLabel && sw.dataset.name) {
      if (addLabel.dataset.tkOrigLabel === undefined) addLabel.dataset.tkOrigLabel = addLabel.textContent.trim();
      addLabel.textContent = addLabel.dataset.tkOrigLabel + ' · ' + sw.dataset.name;
    }
    var galleryImage = card && card.querySelector('.card-gallery img');
    if (galleryImage) {
      if (galleryImage.dataset.tkOrigSrc === undefined) {
        galleryImage.dataset.tkOrigSrc = galleryImage.getAttribute('src') || '';
        galleryImage.dataset.tkOrigSrcset = galleryImage.getAttribute('srcset') || '';
      }
      galleryImage.style.transition = 'opacity 0.18s ease';
      galleryImage.style.opacity = '0.35';
      var undim = function () {
        galleryImage.style.opacity = '';
        galleryImage.removeEventListener('load', undim);
        galleryImage.removeEventListener('error', undim);
      };
      galleryImage.addEventListener('load', undim);
      galleryImage.addEventListener('error', undim);
      if (sw.dataset.imgSrc) {
        galleryImage.src = sw.dataset.imgSrc;
        if (sw.dataset.imgSrcset) galleryImage.setAttribute('srcset', sw.dataset.imgSrcset);
        else galleryImage.removeAttribute('srcset');
      } else {
        galleryImage.src = galleryImage.dataset.tkOrigSrc;
        if (galleryImage.dataset.tkOrigSrcset) galleryImage.setAttribute('srcset', galleryImage.dataset.tkOrigSrcset);
        else galleryImage.removeAttribute('srcset');
      }
      if (galleryImage.complete && galleryImage.naturalWidth > 0) undim();
    }
    if (card) {
      card.classList.add('tk-photo-lock');
      try {
        var slideshow = card.refs && card.refs.slideshow;
        if (slideshow && slideshow.select) slideshow.select(0, undefined, { animate: false });
      } catch (error) {}
    }
    return true;
  }

  /* return a card to the UNRESOLVED state (no ring, group photo, native
     Choose button). Called by tk-color-rail when the shopper goes back to
     "All", and via the rail's bfcache pageshow handler. Cards with fewer
     than 2 colour dots (single-variant stems like Sunflower) are left
     alone — nothing is ambiguous there. */
  function unselectCard(root) {
    if (!root) return;
    var sws = root.querySelectorAll('[data-tk-flc-sw]');
    if (sws.length < 2) return;
    sws.forEach(function (item) {
      item.classList.remove('is-selected');
      item.setAttribute('aria-pressed', 'false');
    });
    var price = root.querySelector('[data-tk-flc-price]');
    if (price && price.dataset.tkOrigPrice !== undefined) price.textContent = price.dataset.tkOrigPrice;
    var name = root.querySelector('[data-tk-flc-selname]');
    if (name && root.dataset.tkFlcUnsel) {
      name.textContent = root.dataset.tkFlcUnsel;
      name.classList.add('is-unset');
    }
    var link = root.querySelector('[data-tk-flc-name]');
    if (link && link.dataset.tkOrigHref !== undefined) link.href = link.dataset.tkOrigHref;
    var card = root.closest('product-card');
    if (card) restoreCardLinks(card);
    var idInput = card && card.querySelector('quick-add-component form input[name="id"]');
    if (idInput && idInput.dataset.tkOrigVid !== undefined) idInput.value = idInput.dataset.tkOrigVid;
    var qa = card && card.querySelector('quick-add-component');
    if (qa) qa.setAttribute('data-quick-add-button', 'choose');
    var addLabel = card && card.querySelector('add-to-cart-component .add-to-cart-text__content span span');
    if (addLabel && addLabel.dataset.tkOrigLabel !== undefined) addLabel.textContent = addLabel.dataset.tkOrigLabel;
    var galleryImage = card && card.querySelector('.card-gallery img');
    if (galleryImage && galleryImage.dataset.tkOrigSrc !== undefined) {
      galleryImage.src = galleryImage.dataset.tkOrigSrc;
      if (galleryImage.dataset.tkOrigSrcset) galleryImage.setAttribute('srcset', galleryImage.dataset.tkOrigSrcset);
      else galleryImage.removeAttribute('srcset');
      galleryImage.style.opacity = '';
    }
  }
  window.tkFlcUnselect = unselectCard;

  function ensureModal() {
    if (modal && modal.isConnected) return true;
    modal = document.createElement('dialog');
    modal.className = 'tk-flc-modal';
    modal.setAttribute('data-tk-flc-modal', '');
    modal.setAttribute('aria-labelledby', 'tk-flc-modal-title');
    modal.innerHTML = '<div class="tk-flc-modal__panel"><button type="button" class="tk-flc-modal__close" data-tk-flc-modal-close><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button><div class="tk-flc-modal__head"><img class="tk-flc-modal__preview" data-tk-flc-modal-preview alt="" width="220" height="220"><div class="tk-flc-modal__meta"><p class="tk-flc-modal__kicker" data-tk-flc-modal-kicker></p><h2 class="tk-flc-modal__title" id="tk-flc-modal-title" data-tk-flc-modal-title></h2><p class="tk-flc-modal__price" data-tk-flc-modal-price></p><p class="tk-flc-modal__chosen"><span data-tk-flc-modal-option></span>: <strong data-tk-flc-modal-chosen></strong></p></div></div><div class="tk-flc-modal__swatches" data-tk-flc-modal-swatches role="group"></div><div class="tk-flc-modal__actions"><button type="button" class="tk-flc-modal__add" data-tk-flc-modal-add></button><a class="tk-flc-modal__link" data-tk-flc-modal-link></a></div><span class="tk-flc-modal__status" data-tk-flc-modal-status aria-live="polite"></span></div>';
    document.body.appendChild(modal);
    modalGrid = modal.querySelector('[data-tk-flc-modal-swatches]');
    modalPreview = modal.querySelector('[data-tk-flc-modal-preview]');
    modalKicker = modal.querySelector('[data-tk-flc-modal-kicker]');
    modalTitle = modal.querySelector('[data-tk-flc-modal-title]');
    modalPrice = modal.querySelector('[data-tk-flc-modal-price]');
    modalOption = modal.querySelector('[data-tk-flc-modal-option]');
    modalChosen = modal.querySelector('[data-tk-flc-modal-chosen]');
    modalAdd = modal.querySelector('[data-tk-flc-modal-add]');
    modalLink = modal.querySelector('[data-tk-flc-modal-link]');
    modalStatus = modal.querySelector('[data-tk-flc-modal-status]');
    modalClose = modal.querySelector('[data-tk-flc-modal-close]');
    modal.addEventListener('click', onModalClick);
    modal.addEventListener('close', cleanupModal);
    return true;
  }

  function labelsFor(el) {
    var d = (el && el.dataset) || {};
    return {
      choose: d.labelChoose || 'Choose a colour',
      option: d.labelOption || 'Colour',
      add: d.labelAdd || 'Add to cart',
      adding: d.labelAdding || 'Adding...',
      added: d.labelAdded || 'Added',
      view: d.labelView || 'View more details',
      close: d.labelClose || 'Close dialog',
      error: d.labelError || 'Error adding to cart',
      soldOut: d.labelSoldOut || 'Sold out'
    };
  }

  function setScrollLock(locked) {
    if (locked) {
      if (lockedScroller) return;
      lockedScroller = document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
      lockedOverflow = lockedScroller.style.overflowY || '';
      lockedScroller.style.overflowY = 'hidden';
    } else if (lockedScroller) {
      lockedScroller.style.overflowY = lockedOverflow;
      lockedScroller = null;
    }
  }

  function updateModal(source) {
    if (!source || !activeLabels) return;
    activeSource = source;
    modalGrid.querySelectorAll('.tk-flc-modal__sw').forEach(function (button) {
      var selected = button.__tkSource === source;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    var image = source.dataset.imgSrc || source.dataset.thumbSrc || '';
    if (image) modalPreview.src = image;
    else modalPreview.removeAttribute('src');
    modalPreview.alt = modalTitle.textContent + (source.dataset.name ? ' — ' + source.dataset.name : '');
    modalPrice.textContent = source.dataset.price || '';
    modalChosen.textContent = source.dataset.name || '';
    modalLink.href = source.getAttribute('href') || '#';
    modalAdd.disabled = source.dataset.available === 'false';
    modalAdd.textContent = modalAdd.disabled ? activeLabels.soldOut : activeLabels.add;
    modalStatus.textContent = '';
  }

  /* sheet state for an UNDECIDED card: group photo as the preview, the
     "choose from N available" line, and Add disabled until a pick — the
     same no-pre-selection honesty rule the tile follows. */
  function setModalUnresolved(root, card) {
    activeSource = null;
    modalGrid.querySelectorAll('.tk-flc-modal__sw').forEach(function (button) {
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
    });
    var galleryImage = card && card.querySelector('.card-gallery img');
    var src = galleryImage && (galleryImage.currentSrc || galleryImage.src);
    if (src) modalPreview.src = src;
    else modalPreview.removeAttribute('src');
    modalPreview.alt = modalTitle.textContent;
    var price = root.querySelector('[data-tk-flc-price]');
    modalPrice.textContent = price ? price.textContent : '';
    modalChosen.textContent = root.dataset.tkFlcUnsel || '';
    var link = root.querySelector('[data-tk-flc-name]');
    modalLink.href = (link && link.getAttribute('href')) || '#';
    modalAdd.disabled = true;
    modalAdd.textContent = activeLabels.add;
    modalStatus.textContent = '';
  }

  function openModal(opener) {
    if (!ensureModal()) return;
    // the opener can be the card's own "+N" chip (inside [data-tk-flc]) or
    // the native Choose button (inside the card gallery) — resolve both
    var root = opener.closest('[data-tk-flc]');
    var card = null;
    if (root) {
      card = root.closest('product-card');
    } else {
      card = opener.closest('product-card');
      root = card && card.querySelector('[data-tk-flc]');
    }
    if (!root || !card) return;
    cancelPendingAdd();
    activeRoot = root;
    activeCard = card;
    activeOpener = opener;
    activeLabels = labelsFor(root.dataset.labelAdd ? root : opener);
    modalClose.setAttribute('aria-label', activeLabels.close);
    modalKicker.textContent = activeLabels.choose;
    modalTitle.textContent = (root.querySelector('[data-tk-flc-name]') || {}).textContent || '';
    modalOption.textContent = activeLabels.option;
    modalGrid.setAttribute('aria-label', activeLabels.choose);
    modalLink.textContent = activeLabels.view;
    modalAdd.textContent = activeLabels.add;
    modalStatus.textContent = '';
    modalGrid.textContent = '';
    var selected = root.querySelector('[data-tk-flc-sw].is-selected');
    root.querySelectorAll('[data-tk-flc-sw]').forEach(function (source) {
      var available = source.dataset.available !== 'false';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'tk-flc-modal__sw';
      button.__tkSource = source;
      button.setAttribute('aria-pressed', 'false');
      var label = source.getAttribute('aria-label') || source.dataset.name || '';
      if (!available) label += ' — ' + activeLabels.soldOut;
      button.setAttribute('aria-label', label);
      button.disabled = !available;
      var thumb = source.dataset.thumbSrc || source.dataset.imgSrc || '';
      if (thumb) {
        var image = document.createElement('img');
        image.src = thumb;
        image.alt = '';
        image.width = 200;
        image.height = 200;
        image.loading = 'lazy';
        image.decoding = 'async';
        button.appendChild(image);
      }
      var check = document.createElement('span');
      check.className = 'tk-flc-modal__check';
      check.setAttribute('aria-hidden', 'true');
      check.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l4.5 4.5L20 5.5"/></svg>';
      button.appendChild(check);
      modalGrid.appendChild(button);
    });
    // no-pre-selection rule: only a colour the shopper (or the rail) actually
    // committed arrives pre-ticked; an undecided card opens with nothing
    // selected and Add disabled until a pick.
    var initial = selected && selected.dataset.available !== 'false' ? selected : null;
    if (initial) updateModal(initial);
    else setModalUnresolved(root, card);
    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    setScrollLock(true);
    // focus a swatch ONLY if it's genuinely selected — focusing the first
    // swatch on an undecided card draws the focus ring around it, which
    // reads as "this colour is picked" while Add sits disabled (Harry's
    // 2026-07-16 report). Undecided sheets focus the close button instead.
    var focusTarget = modalGrid.querySelector('.tk-flc-modal__sw.is-selected:not(:disabled)');
    if (focusTarget) focusTarget.focus();
    else modalClose.focus();
  }

  function cancelPendingAdd() {
    addToken += 1;
    if (cartListener) document.removeEventListener('shopify:cart:lines-update', cartListener);
    cartListener = null;
    clearTimeout(cartTimeout);
    clearTimeout(feedbackTimeout);
    cartTimeout = null;
    feedbackTimeout = null;
  }

  function finishModalAdd(token, successful) {
    if (token !== addToken || !modal || !modal.open || !activeLabels) return;
    if (cartListener) document.removeEventListener('shopify:cart:lines-update', cartListener);
    cartListener = null;
    clearTimeout(cartTimeout);
    cartTimeout = null;
    modalAdd.disabled = false;
    modalAdd.removeAttribute('aria-busy');
    modalAdd.classList.toggle('is-added', successful);
    modalAdd.textContent = successful ? activeLabels.added + ' ✓' : activeLabels.error;
    modalStatus.textContent = successful ? activeLabels.added + ': ' + (activeSource.dataset.name || '') : activeLabels.error;
    feedbackTimeout = setTimeout(function () {
      if (token !== addToken || !modal || !modal.open || !activeLabels) return;
      modalAdd.classList.remove('is-added');
      modalAdd.textContent = activeLabels.add;
      modalStatus.textContent = '';
    }, successful ? 1400 : 1900);
  }

  function addFromModal() {
    if (!activeCard || !activeSource || !activeLabels || modalAdd.disabled || modalAdd.getAttribute('aria-busy') === 'true') return;
    var nativeAdd = activeCard.querySelector('quick-add-component add-to-cart-component button[type="submit"]');
    if (!nativeAdd || nativeAdd.disabled) {
      modalAdd.textContent = activeLabels.error;
      modalStatus.textContent = activeLabels.error;
      return;
    }
    cancelPendingAdd();
    var token = addToken;
    modalAdd.setAttribute('aria-busy', 'true');
    modalAdd.disabled = true;
    modalAdd.textContent = activeLabels.adding;
    cartListener = function (event) {
      var promise = event && event.promise;
      if (promise && typeof promise.then === 'function') {
        Promise.resolve(promise).then(function () { finishModalAdd(token, true); }, function () { finishModalAdd(token, false); });
      } else {
        finishModalAdd(token, true);
      }
    };
    document.addEventListener('shopify:cart:lines-update', cartListener);
    cartTimeout = setTimeout(function () { finishModalAdd(token, false); }, 8000);
    try {
      nativeAdd.click();
    } catch (error) {
      finishModalAdd(token, false);
    }
  }

  function cleanupModal() {
    cancelPendingAdd();
    setScrollLock(false);
    var opener = activeOpener;
    activeRoot = null;
    activeCard = null;
    activeOpener = null;
    activeSource = null;
    activeLabels = null;
    if (opener && opener.isConnected) opener.focus();
  }

  function closeModal() {
    if (!modal) return;
    if (typeof modal.close === 'function' && modal.open) modal.close();
    else {
      modal.removeAttribute('open');
      cleanupModal();
    }
  }

  function onModalClick(event) {
    if (event.target.closest('[data-tk-flc-modal-close]')) {
      closeModal();
      return;
    }
    var swatch = event.target.closest('.tk-flc-modal__sw');
    if (swatch && modalGrid.contains(swatch) && !swatch.disabled) {
      var source = swatch.__tkSource;
      if (selectSwatch(source)) updateModal(source);
      return;
    }
    if (event.target.closest('[data-tk-flc-modal-add]')) {
      addFromModal();
      return;
    }
    if (event.target === modal && event.detail !== 0) closeModal();
  }

  /* Redirect Horizon's native Choose button into the tk colour sheet.
     Primary mechanism: override `handleClick` on each flower card's
     quick-add-component INSTANCE (Horizon's own dispatcher then calls us —
     see header comment for why an event listener cannot win the race).
     The `__tkFlcSeen` flag on the event keeps the belt-and-braces capture
     listener below from opening the sheet a second time for one click. */
  function flowerRootFor(el) {
    var card = el && el.closest && el.closest('product-card');
    var root = card && card.querySelector('[data-tk-flc]');
    if (!root || root.querySelectorAll('[data-tk-flc-sw]').length < 2) return null;
    return root;
  }

  function patchQuickAdd(qa) {
    if (!qa || qa.__tkChoosePatch) return;
    // not yet upgraded to Horizon's class: its constructor would overwrite
    // our override with the class-field handleClick — a later pass patches it
    if (typeof qa.handleClick !== 'function') return;
    if (!flowerRootFor(qa)) return;
    qa.__tkChoosePatch = 1;
    qa.handleClick = function (event) {
      if (event) {
        if (event.__tkFlcSeen) return;
        event.__tkFlcSeen = 1;
        if (typeof event.preventDefault === 'function') event.preventDefault();
      }
      openModal(qa.querySelector('.quick-add__button--choose') || qa);
    };
  }

  function patchAllQuickAdds() {
    document.querySelectorAll('quick-add-component').forEach(patchQuickAdd);
  }

  customElements.whenDefined('quick-add-component').then(patchAllQuickAdds);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchAllQuickAdds, { once: true });
  } else {
    patchAllQuickAdds();
  }
  document.addEventListener('shopify:section:load', patchAllQuickAdds);

  /* Belt-and-braces: if a Choose click reaches an UNPATCHED component
     (straggler created outside the hooks above), open the sheet here and
     close Horizon's stock dialog when its async fetch pops it open. */
  document.addEventListener('click', function (event) {
    var choose = event.target.closest && event.target.closest('.quick-add__button--choose');
    if (!choose) return;
    if (!flowerRootFor(choose)) return;
    event.preventDefault();
    event.stopPropagation();
    var qa = choose.closest('quick-add-component');
    var wasPatched = qa && qa.__tkChoosePatch;
    patchQuickAdd(qa);
    if (event.__tkFlcSeen) return;
    event.__tkFlcSeen = 1;
    openModal(choose);
    if (!wasPatched) {
      var tries = 0;
      var watchdog = setInterval(function () {
        tries += 1;
        var dlg = document.getElementById('quick-add-dialog');
        var open = dlg && dlg.querySelector('dialog[open]');
        if (open && typeof dlg.closeDialog === 'function') {
          dlg.closeDialog();
          clearInterval(watchdog);
        } else if (tries > 25) {
          clearInterval(watchdog);
        }
      }, 200);
    }
  }, true);

  document.addEventListener('click', function (event) {
    var more = event.target.closest && event.target.closest('[data-tk-flc-more]');
    if (more) {
      event.preventDefault();
      openModal(more);
      return;
    }
    var swatch = event.target.closest && event.target.closest('[data-tk-flc-sw]');
    if (!swatch) return;
    event.preventDefault();
    selectSwatch(swatch);
  });

  document.addEventListener('keydown', function (event) {
    if ((event.key === 'Escape' || event.keyCode === 27) && modal && modal.hasAttribute('open') && typeof modal.showModal !== 'function') {
      closeModal();
    }
  });

  /* canary: if a Horizon update renames the stock Choose button class, the
     interception above silently stops matching — surface it loudly. */
  var canary = function () {
    var root = document.querySelector('product-card [data-tk-flc]');
    if (!root || root.querySelectorAll('[data-tk-flc-sw]').length < 2) return;
    var card = root.closest('product-card');
    var qa = card && card.querySelector('quick-add-component');
    if (qa && !qa.querySelector('.quick-add__button--choose')) {
      console.warn('[tk-flcard] .quick-add__button--choose missing — Horizon may have renamed it; the flower Choose flow is inactive.');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', canary, { once: true });
  else canary();
}
